import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { AuthResponse, gameApi, GameSummary, UserRole } from '../api';
import { AdminDebugPanel } from './AdminDebugPanel';
import { usePolling } from '../utils/usePolling';
import { isAbortError } from '../utils/isAbortError';

interface HomeViewProps {
  playerName: string;
  authRole: UserRole | null;
  currentGameId?: string;
  isAuthenticated: boolean;
  onNavigateToMultiplayer: () => void;
  onNavigateToSolo: () => void;
  onSelectGame: (gameId: string) => void;
  onOpenAdminGames: () => void;
  onOpenStatePerformance?: () => void;
  onAuthChanged: (auth: AuthResponse) => void;
  onMessage: (message: string) => void;
}

type GamesTab = 'running' | 'finished';

const formatUserId = (id?: string | null) => {
  if (!id) return '';
  return id.length > 8 ? `${id.slice(0, 8)}...` : id;
};

const isCurrentPlayerTurnInGames = (games: GameSummary[], playerName: string): boolean => {
  const normalizedPlayerName = playerName.trim().toLowerCase();
  if (!normalizedPlayerName) {
    return false;
  }

  return games.some(
    g =>
      g.status === 'WaitingForAction' &&
      !!g.activePlayer &&
      g.activePlayer.trim().toLowerCase() === normalizedPlayerName
  );
};

export const HomeView: React.FC<HomeViewProps> = ({
  playerName,
  authRole,
  currentGameId,
  isAuthenticated,
  onNavigateToMultiplayer,
  onNavigateToSolo,
  onSelectGame,
  onOpenAdminGames,
  onOpenStatePerformance = () => undefined,
  onAuthChanged,
  onMessage,
}) => {
  const multiplayerLabel = isAuthenticated ? 'Create multiplayer game' : 'Log in to create a multiplayer game';
  const [gamesLoading, setGamesLoading] = useState<boolean>(false);
  const [gamesLoaded, setGamesLoaded] = useState<boolean>(false);
  const [gamesError, setGamesError] = useState<string | null>(null);
  const [availableGames, setAvailableGames] = useState<GameSummary[]>([]);
  const [hasCurrentPlayerTurn, setHasCurrentPlayerTurn] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<GamesTab>('running');
  const [focusedTab, setFocusedTab] = useState<GamesTab>('running');
  const tabsId = useId();
  const tabRefs = useRef<Partial<Record<GamesTab, HTMLButtonElement | null>>>({});
  const selectTab = (tab: GamesTab) => {
    setFocusedTab(tab);
    if (tab !== activeTab) {
      setGamesLoaded(false);
      setActiveTab(tab);
    }
  };
  const handleTabKey = (event: React.KeyboardEvent<HTMLButtonElement>, tab: GamesTab) => {
    let target: GamesTab;
    if (event.key === 'Home') target = 'running';
    else if (event.key === 'End') target = 'finished';
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') target = tab === 'running' ? 'finished' : 'running';
    else return;
    event.preventDefault();
    setFocusedTab(target);
    tabRefs.current[target]?.focus();
  };
  const fetchGames = useCallback(async (signal?: AbortSignal) => {
    setGamesLoading(true);
    setGamesError(null);
    try {
      const name = playerName.trim();
      if (activeTab === 'running') {
        const games = await gameApi.listGames(name || undefined, signal);
        setAvailableGames(games);
        setHasCurrentPlayerTurn(isCurrentPlayerTurnInGames(games, name));
      } else {
        const [finishedGames, runningGames] = await Promise.all([
          gameApi.listFinishedGames(name || undefined, signal),
          gameApi.listGames(name || undefined, signal),
        ]);
        setAvailableGames(finishedGames);
        setHasCurrentPlayerTurn(isCurrentPlayerTurnInGames(runningGames, name));
      }
      setGamesLoaded(true);
    } catch (err) {
      if (isAbortError(err, signal)) {
        return;
      }
      setGamesError(err instanceof Error ? err.message : 'Failed to load games');
      setHasCurrentPlayerTurn(false);
    } finally {
      setGamesLoading(false);
    }
  }, [activeTab, playerName]);

  useEffect(() => {
    document.title = hasCurrentPlayerTurn ? '* Arkham Horror' : 'Arkham Horror';
    return () => {
      document.title = 'Arkham Horror';
    };
  }, [hasCurrentPlayerTurn]);

  const handleDeleteGame = useCallback(
    async (gameId: string) => {
      if (!isAuthenticated) {
        setGamesError('Login is required to delete game');
        return;
      }
      const owner = playerName.trim();
      if (!gameId || !owner) return;

      if (!window.confirm("Are you sure you want to delete this game?")) {
        return;
      }

      setGamesError(null);
      setGamesLoading(true);
      try {
        await gameApi.deleteGame(gameId, owner);
        setAvailableGames(prev => prev.filter(g => g.gameId !== gameId));
      } catch (err) {
        setGamesError(err instanceof Error ? err.message : 'Failed to delete game');
      } finally {
        setGamesLoading(false);
      }
    },
    [isAuthenticated, playerName]
  );

  usePolling({
    fetcher: fetchGames,
    intervalMs: 5000,
    hiddenDelayMs: 60000,
  });

  const games = availableGames.map(game => {
    const currentName = playerName.trim().toLowerCase();
    const isOwner = !!game.owner && !!currentName && game.owner.toLowerCase() === currentName;
    const ownerId = formatUserId(game.ownerUserId);
    const owner = game.owner ?? (ownerId ? `User ${ownerId}` : '-');
    return {
      game,
      ownerLabel: isOwner ? `${owner} (you)` : owner,
      status: game.status === 'WaitingForAction'
        ? (game.activePlayer ? `${game.activePlayer}'s turn` : 'Current turn')
        : game.status,
      canDelete: activeTab === 'running' && isOwner,
    };
  });

  const handleGameLink = (event: React.MouseEvent<HTMLAnchorElement>, gameId: string) => {
    event.stopPropagation();
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
    event.preventDefault();
    onSelectGame(gameId);
  };

  const renderPlayers = (game: GameSummary) => (
    <>
      {game.expectedPlayersCount === 1 ? 'Solo' : `${game.playersCount}/${game.expectedPlayersCount}`}
      {game.players.length > 0 && (
        <div className="player-list">
          {game.players.map(name => <span key={name}>{name}</span>)}
        </div>
      )}
    </>
  );

  const renderDelete = (gameId: string) => (
    <button type="button" className="delete-button compact-button" disabled={gamesLoading}
      onClick={event => { event.stopPropagation(); handleDeleteGame(gameId); }}>
      Delete
    </button>
  );

  return (
    <div className="home-menu entry-home">
      <h1>Arkham Horror</h1>
      <p className="home-edition">Second edition (2005)</p>
      <p className="home-introduction">Play Arkham Horror Second Edition solo or with friends.</p>
      <div className="home-actions">
        <div className="home-action-choice">
          <button className="entry-primary-action" onClick={onNavigateToSolo}>Play solo</button>
        </div>
        <div className="home-action-choice">
          <button className="entry-primary-action" onClick={onNavigateToMultiplayer}>{multiplayerLabel}</button>
        </div>
      </div>
      <div className="games-table">
        <div className="games-tabs" role="tablist" aria-label="Game lists" onBlur={event => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocusedTab(activeTab);
        }}>
          <button
            className={`games-tab ${activeTab === 'running' ? 'active' : ''}`}
            aria-selected={activeTab === 'running'}
            role="tab"
            id={`${tabsId}-running`}
            aria-controls={`${tabsId}-panel`}
            tabIndex={focusedTab === 'running' ? 0 : -1}
            ref={element => { tabRefs.current.running = element; }}
            onFocus={() => setFocusedTab('running')}
            onKeyDown={event => handleTabKey(event, 'running')}
            onClick={() => selectTab('running')}
          >
            Active Games
          </button>
          <button
            className={`games-tab ${activeTab === 'finished' ? 'active' : ''}`}
            role="tab"
            aria-selected={activeTab === 'finished'}
            id={`${tabsId}-finished`}
            aria-controls={`${tabsId}-panel`}
            tabIndex={focusedTab === 'finished' ? 0 : -1}
            ref={element => { tabRefs.current.finished = element; }}
            onFocus={() => setFocusedTab('finished')}
            onKeyDown={event => handleTabKey(event, 'finished')}
            onClick={() => selectTab('finished')}
          >
            Finished Games
          </button>
        </div>
        <div role="tabpanel" id={`${tabsId}-panel`} aria-labelledby={`${tabsId}-${activeTab}`} tabIndex={0}>
        {gamesLoading && !gamesLoaded && <div>Loading games...</div>}
        {gamesError && <div className="error">{gamesError}</div>}
        {gamesLoaded && !gamesError && availableGames.length === 0 && (
          <div className="home-empty-state">
            <p>{activeTab === 'running' ? 'No active games yet.' : 'No finished games yet. Completed games will appear here.'}</p>
            {activeTab === 'finished' && <div className="home-empty-actions">
              <button className="entry-secondary-action" onClick={() => {
                selectTab('running');
                tabRefs.current.running?.focus();
              }}>View active games</button>
            </div>}
          </div>
        )}
        {gamesLoaded && availableGames.length > 0 && (
          <>
            <div className="games-table-wrapper">
              <table>
                <thead><tr>
                  {activeTab === 'running' ? <><th scope="col">Owner</th><th scope="col">Status</th></> : <th scope="col">Result</th>}
                  <th scope="col">Players</th>
                  {activeTab === 'running' && <th scope="col">Actions</th>}
                </tr></thead>
                <tbody>
                  {games.map(({ game, ownerLabel, status, canDelete }) => (
                    <tr key={game.gameId} onClick={event => {
                      if (event.button === 0 && !event.metaKey && !event.ctrlKey && !event.altKey && !event.shiftKey) onSelectGame(game.gameId);
                    }}>
                      {activeTab === 'running' ? <>
                        <td><a href={`/game/${game.gameId}`} onClick={event => handleGameLink(event, game.gameId)}>{ownerLabel}</a></td>
                        <td><a href={`/game/${game.gameId}`} onClick={event => handleGameLink(event, game.gameId)}>{status}</a></td>
                      </> : <td><a href={`/game/${game.gameId}`} onClick={event => handleGameLink(event, game.gameId)}>{game.result || '-'}</a></td>}
                      <td><a href={`/game/${game.gameId}`} onClick={event => handleGameLink(event, game.gameId)}>{renderPlayers(game)}</a></td>
                      {activeTab === 'running' && <td>{canDelete && renderDelete(game.gameId)}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ul className="home-games-list" aria-label={activeTab === 'running' ? 'Active games' : 'Finished games'}>
              {games.map(({ game, ownerLabel, status, canDelete }) => (
                <li key={game.gameId} className="home-game-entry">
                  <a className="home-game-details" href={`/game/${game.gameId}`} onClick={event => handleGameLink(event, game.gameId)}>
                    {activeTab === 'running' ? <><span className="home-game-owner">{ownerLabel}</span><span>{status}</span></> : <span className="home-game-result">{game.result || '-'}</span>}
                    <div className="home-game-players">{renderPlayers(game)}</div>
                  </a>
                  {canDelete && renderDelete(game.gameId)}
                </li>
              ))}
            </ul>
          </>
        )}
        </div>
        {authRole === 'Admin' && (
          <AdminDebugPanel
            currentGameId={currentGameId}
            isAuthenticated={isAuthenticated}
            onOpenAdminGames={onOpenAdminGames}
            onOpenStatePerformance={onOpenStatePerformance}
            onAuthChanged={onAuthChanged}
            onMessage={onMessage}
          />
        )}
      </div>
      <footer className="home-version">v 3.0.27</footer>
    </div>
  );
};
